import {
  extractMessageFromErrorResponseBody,
  przezmorzeApi,
  throwMessageOnBadRequestStatus,
  throwMessageOnStatuses,
} from "../../api/api";
import type { CalculationType, CalculationVersion, SavedCalculation } from "./calculationTypes";
import type {
  Calculation as CalculationResponseDTO,
  CalculationAvailabilityReport,
  ProductAvailabilityReport,
  AvailabilityProduct,
  UpdateCalculationPricesResponse,
} from "@przezmorze/api-types";

export type { ProductAvailabilityReport as ProductAvailabilityReportDTO };
export type CalculationAvailabilityReportDTO = CalculationAvailabilityReport;

export type CreateCalculationRequest = CalculationVersion & {
  readonly customerId: string;
  readonly type: CalculationType;
};

function extractCreateOrUpdateCalculationErrorMessage(responseBody: unknown) {
  if (
    typeof responseBody === "object" &&
    responseBody !== null &&
    "errorCode" in responseBody &&
    responseBody.errorCode === "PRICE_UNAVAILABLE"
  ) {
    return "Brak możliwości realizacji pakietu w wybranym terminie, przynajmniej jeden z produktów jest niedostępny. Zmień termin wyjazdu";
  }
  return extractMessageFromErrorResponseBody(responseBody);
}

function extractOrderCalculationErrorMessage(responseBody: unknown) {
  if (
    typeof responseBody === "object" &&
    responseBody !== null &&
    "errorCode" in responseBody &&
    responseBody.errorCode === "INVALID_OPTION_PRICE"
  ) {
    return "Cena u dostawcy uległa zmianie. Zaktualizuj ceny w kalkulacji i spróbuj ponownie.";
  }
  return extractMessageFromErrorResponseBody(responseBody);
}

export async function createCalculation(calculation: CreateCalculationRequest, abortSignal?: AbortSignal) {
  try {
    const data = {
      name: calculation.name,
      versionName: calculation.versionName,
      packageId: calculation.packageId,
      customerId: calculation.customerId,
      type: calculation.type,
      startDate: calculation.startDate,
      currency: calculation.currency,
      margin: calculation.margin,
      participantsNumber: calculation.participantsNumber,
      note: calculation.note,
      products: calculation.products,
    };
    const response = await przezmorzeApi.post<CalculationResponseDTO>("/calculations", data, {
      signal: abortSignal,
    });
    const { customer, ...others } = response.data;
    return { ...others, customerId: customer.id };
  } catch (error) {
    throwMessageOnBadRequestStatus(error, extractCreateOrUpdateCalculationErrorMessage);
  }
}

export async function updateCalculation(
  calculationId: string,
  calculation: CalculationVersion,
  abortSignal?: AbortSignal,
): Promise<SavedCalculation> {
  try {
    const response = await przezmorzeApi.put<CalculationResponseDTO>(
      `/calculations/${calculationId}`,
      {
        name: calculation.name,
        versionName: calculation.versionName,
        packageId: calculation.packageId,
        startDate: calculation.startDate,
        currency: calculation.currency,
        margin: calculation.margin,
        participantsNumber: calculation.participantsNumber,
        note: calculation.note,
        products: calculation.products,
      },
      {
        signal: abortSignal,
      },
    );
    const { customer, ...others } = response.data;
    return { ...others, customerId: customer.id };
  } catch (error) {
    throwMessageOnBadRequestStatus(error, extractCreateOrUpdateCalculationErrorMessage);
  }
}

export async function updateCalculationPrices(
  calculationId: string,
  abortSignal?: AbortSignal,
): Promise<{ calculation: SavedCalculation; wasModified: boolean }> {
  try {
    const response = await przezmorzeApi.put<UpdateCalculationPricesResponse>(
      `/calculations/${calculationId}/prices`,
      undefined,
      { signal: abortSignal },
    );
    const { calculation, wasModified } = response.data;
    const { customer, ...others } = calculation;
    return { wasModified, calculation: { ...others, customerId: customer.id } };
  } catch (error) {
    throwMessageOnBadRequestStatus(error, extractCreateOrUpdateCalculationErrorMessage);
  }
}

export async function checkCalculationAvailability(
  products: AvailabilityProduct[],
  participantsNumber: number,
  signal?: AbortSignal,
): Promise<CalculationAvailabilityReport> {
  const response = await przezmorzeApi.post<CalculationAvailabilityReport>(
    "/calculations/availability",
    { products, participantsNumber },
    { signal },
  );
  return response.data;
}

export async function orderACalculation(
  calculationId: string,
  version: number,
  orderFromProviders: boolean,
  abortSignal?: AbortSignal,
): Promise<SavedCalculation> {
  try {
    const response = await przezmorzeApi.post<CalculationResponseDTO>(
      `/calculations/${calculationId}/order`,
      { version, orderFromProviders },
      {
        signal: abortSignal,
      },
    );
    const { customer, ...others } = response.data;
    return { ...others, customerId: customer.id };
  } catch (error) {
    throwMessageOnStatuses(error, [400, 409], "Nie udało się złożyć zamówienia", extractOrderCalculationErrorMessage);
  }
}

export async function getCalculation(calculationId: string, abortSignal?: AbortSignal): Promise<SavedCalculation> {
  const response = await przezmorzeApi.get<CalculationResponseDTO>(`/calculations/${calculationId}`, {
    signal: abortSignal,
  });
  const { customer, ...others } = response.data;
  return { ...others, customerId: customer.id };
}

export async function deleteCalculation(calculationId: string, abortSignal?: AbortSignal): Promise<void> {
  await przezmorzeApi.delete(`/calculations/${calculationId}`, { signal: abortSignal });
}

export async function getCalculationsCsv(from: string, to: string, abortSignal?: AbortSignal): Promise<Blob> {
  const response = await przezmorzeApi.get<Blob>(`/calculation-export/csv`, {
    signal: abortSignal,
    headers: { Accept: "application/csv" },
    params: { from, to },
    responseType: "blob",
  });
  return response.data;
}
